import { memo, useState } from "react";
import { useSelector } from "react-redux";
import RevstackRangeDatePicker from "./RevstackRangeDatePicker";
import { Button, Popover } from "antd";
import { BsChevronDown } from "react-icons/bs";
import dayjs from "dayjs";
import { DATE_PICKER_PRESET_PERIODS, DateTimeFormats } from "@ts/constants";
import { TSelectedDateRange } from "@ts/common-types";

interface DateRange {
    start: dayjs.Dayjs | null;
    end: dayjs.Dayjs | null;
}

interface RevstackDatePickerProps {
    editable?: boolean;
}

const RevstackDatePicker = memo(({ editable = true }: RevstackDatePickerProps) => {
    const [showPicker, setShowPicker] = useState(false);

    const selectedDateRange = useSelector(
        (state: { selectedDateRange: { value: TSelectedDateRange } }) => state.selectedDateRange.value
    );

    // Convert Redux state to component format
    const selectedRanges = {
        primary: selectedDateRange.dateRange
            ? {
                  start: dayjs(selectedDateRange.dateRange.start),
                  end: dayjs(selectedDateRange.dateRange.end),
              }
            : null,
        compare: selectedDateRange.compareDateRange
            ? {
                  start: dayjs(selectedDateRange.compareDateRange.start),
                  end: dayjs(selectedDateRange.compareDateRange.end),
              }
            : null,
    };

    const handleApply = (
        primaryRange: DateRange,
        compareRange: DateRange | null
    ) => {
        setShowPicker(false);
    };

    const handleCancel = () => {
        setShowPicker(false);
    };

    const formatRange = (range: DateRange | null) => {
        if (!range || !range.start) return "Not selected";
        if (!range.end)
            return range.start.format(DateTimeFormats.DATE_PICKER_DISPLAY);
        return `${range.start.format(
            DateTimeFormats.DATE_PICKER_RANGE
        )} - ${range.end.format(DateTimeFormats.DATE_PICKER_DISPLAY)}`;
    };

    const formatDateButton = () => {
        const primary = selectedRanges?.primary;
        const compare = selectedRanges?.compare;

        if (!primary || !primary.start) return "Select Date Range";

        const primaryText = primary.end
            ? `${primary.start.format(
                  DateTimeFormats.DATE_PICKER_DAY
              )} - ${primary.end.format(
                  DateTimeFormats.DATE_PICKER_DAY
              )} ${formatRange(primary)}`
            : primary.start.format(DateTimeFormats.DATE_PICKER_DAY_FULL);

        const compareText =
            compare && compare.start && compare.end
                ? `Compare: ${formatRange(compare)}`
                : "";

        return { primaryText, compareText };
    };

    const { primaryText, compareText }: any = formatDateButton();

    const popoverContent = (
        <div style={{ width: "800px" }}>
            <RevstackRangeDatePicker
                onApply={handleApply}
                onCancel={handleCancel}
                initialPrimary={selectedRanges?.primary || null}
                initialCompare={selectedRanges?.compare || null}
                initialPresetPeriod={
                    selectedDateRange.selectedRange ||
                    DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS
                }
            />
        </div>
    );

    const dateDisplay = (
        <div className="flex-fill text-start">
            <div
                style={{
                    fontSize: 11,
                    color: "#2D2D2D",
                    fontWeight: 600,
                }}
            >
                {primaryText}
            </div>
            {compareText && (
                <div
                    style={{
                        fontSize: 9,
                        color: "#6B7280",
                        fontWeight: 500,
                    }}
                >
                    {compareText}
                </div>
            )}
        </div>
    );

    if (!editable) {
        return (
            <div
                className="d-flex align-items-center gap-1 rounded-2"
                style={{
                    minWidth: "172px",
                    height: "35px",
                    padding: "1px 5px 1px 5px",
                    backgroundColor: "#F0F4F8",
                }}
            >
                {dateDisplay}
            </div>
        );
    }

    return (
        <Popover
            content={popoverContent}
            title={null}
            trigger="click"
            open={showPicker}
            onOpenChange={setShowPicker}
            placement="bottomLeft"
            overlayStyle={{ padding: 0 }}
            overlayClassName="date-picker-popover"
        >
            <Button
                className="d-flex align-items-center gap-1"
                style={{ minWidth: "200px", padding: "1px 5px 1px 5px" }}
            >
                {dateDisplay}
                <BsChevronDown size={16} className="text-muted" />
            </Button>
        </Popover>
    );
});

RevstackDatePicker.displayName = "RevstackDatePicker";

export default RevstackDatePicker;
